/**
 * Junior's role ladder — mirrors BE/src/services/roleHierarchy.js.
 *
 * Authority only flows down: you can change people, or grant roles, only below your
 * own level; the System Owner can manage anyone. The server enforces this; the
 * screens use it only to hide or grey out actions that would be refused.
 */

export const ROLE_RANK: Record<string, number> = {
  system_owner: 100,
  superadmin: 80,
  admin: 60,
  auditor: 40,
  user: 20,
};

/** System roles, lowest first (dropdown order). */
export const SYSTEM_ROLES = ['user', 'auditor', 'admin', 'superadmin', 'system_owner'] as const;

export const ROLE_LABELS: Record<string, string> = {
  system_owner: 'System Owner',
  superadmin: 'Super Admin',
  admin: 'Admin',
  auditor: 'Auditor',
  user: 'User',
};

export function rankOf(role?: string | null): number {
  return ROLE_RANK[(role ?? '').toLowerCase()] ?? ROLE_RANK.user;
}

export function isSystemOwner(role?: string | null): boolean {
  return (role ?? '').toLowerCase() === 'system_owner';
}

export function isSuperadminOrAbove(role?: string | null): boolean {
  return rankOf(role) >= ROLE_RANK.superadmin;
}

export function canManage(actorRole?: string | null, targetRole?: string | null): boolean {
  return isSystemOwner(actorRole) || rankOf(actorRole) > rankOf(targetRole);
}

export function canGrantRole(actorRole?: string | null, role?: string | null): boolean {
  return isSystemOwner(actorRole) || rankOf(actorRole) > rankOf(role);
}

/** Roles the actor may give someone, lowest first. */
export function grantableRoles(actorRole?: string | null): string[] {
  return SYSTEM_ROLES.filter((r) => canGrantRole(actorRole, r));
}

export function roleLabel(role?: string | null): string {
  return ROLE_LABELS[(role ?? '').toLowerCase()] ?? role ?? 'User';
}
